import { create } from "@bufbuild/protobuf";

import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  CreateSourceRequest,
  CreateSourceRequestSchema,
} from "@gx/ts-contracts/services/source/api/source_pb";
import { SourceConfigurationSchema } from "@gx/ts-contracts/services/source/resources/source_configuration/source_configuration_pb";
import { SourceWikipediaConfigurationSchema } from "@gx/ts-contracts/services/source/resources/source_configuration/wikipedia/wikipedia_pb";

import { isCreateSourceValueProvided } from "@/pages/sources/components/create/sources/utils";
import {
  CREATE_SOURCE_WIKIPEDIA_INVALID_URL_ERROR,
  CREATE_SOURCE_WIKIPEDIA_SEED_URL_REQUIRED_ERROR,
  SOURCE_WIKIPEDIA_CONFIGURATION_CASE,
} from "@/pages/sources/components/create/sources/wikipedia/constants";
import {
  CreateSourceWikipediaState,
  WikipediaPageEstimateSeverity,
} from "@/pages/sources/components/create/sources/wikipedia/types";

const WIKIPEDIA_URL_REGEX = /^https?:\/\/([a-z]{2,3}\.)?wikipedia\.org\/wiki\/.+$/i;

export function isValidWikipediaUrl(url: string): boolean {
  return WIKIPEDIA_URL_REGEX.test(url.trim());
}

export function getCreateSourceWikipediaSeedUrlError(
  state: CreateSourceWikipediaState,
): string | null {
  if (!state.shouldShowErrors) {
    return null;
  }

  if (!isCreateSourceValueProvided(state.seedUrl)) {
    return CREATE_SOURCE_WIKIPEDIA_SEED_URL_REQUIRED_ERROR;
  }

  if (!isValidWikipediaUrl(state.seedUrl)) {
    return CREATE_SOURCE_WIKIPEDIA_INVALID_URL_ERROR;
  }

  return null;
}

/**
 * Calculate estimated pages using geometric series formula:
 * 1 + n + n^2 + ... + n^d = (n^(d+1) - 1) / (n - 1)
 */
export function calculateEstimatedPages(linksPerPage: number, depth: number): number {
  if (depth === 0) return 1;
  if (linksPerPage <= 1) return depth + 1;
  return Math.floor((Math.pow(linksPerPage, depth + 1) - 1) / (linksPerPage - 1));
}

export function getPageEstimateSeverity(
  estimated: number,
  maxPages: number,
): WikipediaPageEstimateSeverity {
  if (estimated > maxPages) return WikipediaPageEstimateSeverity.ERROR;
  if (estimated > 500) return WikipediaPageEstimateSeverity.WARNING;
  if (estimated > 100) return WikipediaPageEstimateSeverity.CAUTION;
  return WikipediaPageEstimateSeverity.SUCCESS;
}

export function buildCreateSourceWikipediaRequest(
  sourceName: string,
  seedUrl: string,
  linksPerPage: number,
  crawlDepth: number,
  maxPages: number,
): CreateSourceRequest {
  return create(CreateSourceRequestSchema, {
    name: sourceName.trim(),
    type: SourceType.WIKIPEDIA,
    configuration: create(SourceConfigurationSchema, {
      configuration: {
        case: SOURCE_WIKIPEDIA_CONFIGURATION_CASE,
        value: create(SourceWikipediaConfigurationSchema, {
          seedUrl: seedUrl.trim(),
          linksPerPage,
          crawlDepth,
          maxPages,
        }),
      },
    }),
  });
}
